import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import { FaSave, FaPlus, FaTrash, FaPen, FaLock, FaUsers, FaUserShield, FaTimes, FaUserPlus, FaAt } from 'react-icons/fa';
import { LuKeyRound, LuChevronDown, LuChevronRight, LuShieldAlert, LuSearch } from 'react-icons/lu';
import instanciaAxios, { esErrorPermiso } from '../../miscelaneo/instanciaAxios';
import SpinnerCargando from '../util/SpinnerCargando';
import DockVerticalDerecha from '../estructura/DockVerticalDerecha';

// Nombre legible para módulos
const NOMBRES_MODULO = {
  venta: 'Ventas',
  compra: 'Compras',
  inventario: 'Inventario',
  entidad: 'Entidades',
  contabilidad: 'Contabilidad',
  configuracion: 'Configuración',
  caja: 'Caja',
  permisos: 'Permisos',
  reportes: 'Reportes',
};

const ES_ADMIN = (rol) => rol?.descripcion === 'administrador';

/**
 * Componente para checkbox de permiso individual
 */
function CheckPermiso({ permiso, checked, disabled, heredado, onChange }) {
  return (
    <label
      className={`flex items-center gap-3 p-2 rounded-lg transition-colors cursor-pointer ${
        disabled ? 'opacity-60 cursor-not-allowed' : 'hover:bg-gray-50'
      }`}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(permiso.id_permiso, e.target.checked)}
        className="w-4 h-4 rounded border-gray-300 text-color-secundario focus:ring-color-secundario"
      />
      <div className="flex-1 min-w-0">
        <span className="text-sm text-gray-700">{permiso.nombre_legible || permiso.clave}</span>
        {heredado && (
          <span className="ml-2 text-xs bg-gray-200 text-gray-500 px-2 py-0.5 rounded-full" title="Heredado del rol">
            heredado
          </span>
        )}
      </div>
    </label>
  );
}

/**
 * Componente para grupo de permisos por módulo (colapsable)
 */
function GrupoModulo({ modulo, permisos, seleccionados, heredados = [], disabled = false, onChange }) {
  const [abierto, setAbierto] = useState(true);

  const todosSeleccionados = permisos.every(
    (p) => seleccionados.includes(p.id_permiso) || heredados.includes(p.id_permiso)
  );

  const handleToggleTodos = (checked) => {
    permisos.forEach((p) => {
      if (!heredados.includes(p.id_permiso)) {
        onChange(p.id_permiso, checked);
      }
    });
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setAbierto(!abierto)}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-3">
          {abierto ? (
            <LuChevronDown className="text-gray-500" />
          ) : (
            <LuChevronRight className="text-gray-500" />
          )}
          <span className="font-semibold text-gray-700">
            {NOMBRES_MODULO[modulo] || modulo}
          </span>
          <span className="text-xs bg-color-primario bg-opacity-10 text-color-primario px-2 py-0.5 rounded-full">
            {permisos.filter((p) => seleccionados.includes(p.id_permiso) || heredados.includes(p.id_permiso)).length}/{permisos.length}
          </span>
        </div>
        {!disabled && (
          <label
            className="flex items-center gap-2 text-sm text-gray-500 cursor-pointer"
            onClick={(e) => e.stopPropagation()}
          >
            <input
              type="checkbox"
              checked={todosSeleccionados}
              onChange={(e) => handleToggleTodos(e.target.checked)}
              className="w-3.5 h-3.5 rounded border-gray-300 text-color-secundario focus:ring-color-secundario"
            />
            Todos
          </label>
        )}
      </button>
      {abierto && (
        <div className="px-4 py-2 space-y-0.5">
          {permisos.map((permiso) => (
            <CheckPermiso
              key={permiso.id_permiso}
              permiso={permiso}
              checked={
                seleccionados.includes(permiso.id_permiso) ||
                heredados.includes(permiso.id_permiso)
              }
              disabled={disabled || heredados.includes(permiso.id_permiso)}
              heredado={heredados.includes(permiso.id_permiso)}
              onChange={onChange}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Grid de permisos agrupados por módulo
 */
function GridPermisos({ agrupados, seleccionados, heredados, disabled, onChange }) {
  const modulos = Object.keys(agrupados).sort();

  return (
    <div className="space-y-3">
      {modulos.map((modulo) => (
        <GrupoModulo
          key={modulo}
          modulo={modulo}
          permisos={agrupados[modulo]}
          seleccionados={seleccionados}
          heredados={heredados}
          disabled={disabled}
          onChange={onChange}
        />
      ))}
    </div>
  );
}

export default function AdministracionPermisos() {
  const [tabActivo, setTabActivo] = useState('roles'); // 'roles' | 'usuarios' | 'gestionRoles'
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);

  // Datos maestros
  const [todosPermisos, setTodosPermisos] = useState([]);
  const [permisosAgrupados, setPermisosAgrupados] = useState({});

  // Tab Roles (permisos)
  const [roles, setRoles] = useState([]);
  const [rolSeleccionado, setRolSeleccionado] = useState(null);
  const [permisosRol, setPermisosRol] = useState([]);
  const [permisosRolOriginal, setPermisosRolOriginal] = useState([]);

  // Tab Usuarios
  const [listaUsuarios, setListaUsuarios] = useState([]);
  const [filtroUsuario, setFiltroUsuario] = useState('');
  const [cargandoUsuarios, setCargandoUsuarios] = useState(false);
  const [usuarioSeleccionado, setUsuarioSeleccionado] = useState(null);
  const [permisosUsuarioIndividuales, setPermisosUsuarioIndividuales] = useState([]);
  const [permisosUsuarioRol, setPermisosUsuarioRol] = useState([]);
  const [nombreRolUsuario, setNombreRolUsuario] = useState('');
  const [idTipoUsuarioActual, setIdTipoUsuarioActual] = useState(null);

  // Tab Gestión de Roles
  const [nuevoRolNombre, setNuevoRolNombre] = useState('');
  const [creandoRol, setCreandoRol] = useState(false);
  const [editandoRolId, setEditandoRolId] = useState(null);
  const [editandoRolNombre, setEditandoRolNombre] = useState('');

  // Tenant info
  const [tenantInfo, setTenantInfo] = useState(null);

  // Modal crear usuario
  const [modalCrearUsuario, setModalCrearUsuario] = useState(false);
  const [creandoUsuario, setCreandoUsuario] = useState(false);
  const [nuevoUsuario, setNuevoUsuario] = useState({
    nombre: '', apellido: '', correo: '', username: '', clave: '', confirmarClave: '', id_tipo_usuario: ''
  });

  // Helper para recargar roles
  const recargarRoles = useCallback(async () => {
    try {
      const rolesRes = await instanciaAxios.get('/permiso/tipo-usuario');
      setRoles(rolesRes.data || []);
      return rolesRes.data || [];
    } catch (error) {
      console.error('Error al recargar roles:', error);
      return [];
    }
  }, []);

  // Cargar lista de usuarios del tenant
  const cargarUsuarios = useCallback(async () => {
    setCargandoUsuarios(true);
    try {
      const res = await instanciaAxios.get('/permiso/usuarios');
      setListaUsuarios(res.data || []);
    } catch (error) {
      console.error('Error al cargar usuarios:', error);
      toast.error('Error al cargar lista de usuarios');
    } finally {
      setCargandoUsuarios(false);
    }
  }, []);

  // Cargar tenant info
  const cargarTenantInfo = useCallback(async () => {
    try {
      const res = await instanciaAxios.get('/permiso/tenant-info');
      setTenantInfo(res.data);
    } catch (error) {
      console.error('Error al cargar info del tenant:', error);
    }
  }, []);

  // Cargar datos maestros
  useEffect(() => {
    const cargar = async () => {
      try {
        const [permisosRes, rolesRes] = await Promise.all([
          instanciaAxios.get('/permiso'),
          instanciaAxios.get('/permiso/tipo-usuario'),
          cargarTenantInfo(),
        ]);

        setTodosPermisos(permisosRes.data.permisos || []);
        setPermisosAgrupados(permisosRes.data.agrupados || {});
        setRoles(rolesRes.data || []);

        // Seleccionar primer rol por defecto
        if (rolesRes.data?.length > 0) {
          const primerRol = rolesRes.data[0];
          const idsPermisos = primerRol.permisos.map((p) => p.id_permiso);
          setRolSeleccionado(primerRol.id_tipo_usuario);
          setPermisosRol(idsPermisos);
          setPermisosRolOriginal(idsPermisos);
        }
      } catch (error) {
        console.error('Error al cargar datos de permisos:', error);
        const msg = error.response?.data?.error || 'Error al cargar datos de permisos';
        const faltantes = error.response?.data?.permisos_faltantes;
        if (faltantes) {
          toast.error(`${msg}. Permisos faltantes: ${faltantes.join(', ')}`, { autoClose: 10000 });
        } else {
          toast.error(msg);
        }
      } finally {
        setCargando(false);
      }
    };
    cargar();
  }, [cargarTenantInfo]);

  // Cargar usuarios cuando se activa el tab
  useEffect(() => {
    if (tabActivo === 'usuarios' && listaUsuarios.length === 0) {
      cargarUsuarios();
    }
  }, [tabActivo, listaUsuarios.length, cargarUsuarios]);

  // Obtener el rol seleccionado como objeto
  const rolSeleccionadoObj = roles.find(r => r.id_tipo_usuario === rolSeleccionado);
  const esAdminSeleccionado = ES_ADMIN(rolSeleccionadoObj);

  // Usuarios filtrados por texto
  const usuariosFiltrados = useMemo(() => {
    if (!filtroUsuario.trim()) return listaUsuarios;
    const lower = filtroUsuario.toLowerCase();
    return listaUsuarios.filter((u) =>
      `${u.nombre} ${u.apellido}`.toLowerCase().includes(lower) ||
      u.usuario?.toLowerCase().includes(lower) ||
      u.nombre_rol?.toLowerCase().includes(lower)
    );
  }, [listaUsuarios, filtroUsuario]);

  // Al cambiar de rol, cargar sus permisos
  const handleCambiarRol = useCallback(async (idTipoUsuario) => {
    setRolSeleccionado(idTipoUsuario);
    try {
      const res = await instanciaAxios.get(`/permiso/tipo-usuario/${idTipoUsuario}`);
      const idsPermisos = res.data.permisos.map((p) => p.id_permiso);
      setPermisosRol(idsPermisos);
      setPermisosRolOriginal(idsPermisos);
    } catch (error) {
      toast.error('Error al cargar permisos del rol');
    }
  }, []);

  // Toggle permiso en lista de seleccionados (Tab Roles)
  const handleTogglePermisoRol = useCallback((idPermiso, checked) => {
    setPermisosRol((prev) =>
      checked ? [...prev, idPermiso] : prev.filter((id) => id !== idPermiso)
    );
  }, []);

  // Guardar permisos del rol
  const handleGuardarRol = async () => {
    if (guardando || !rolSeleccionado) return;
    setGuardando(true);
    try {
      await instanciaAxios.put(`/permiso/tipo-usuario/${rolSeleccionado}`, {
        permisos: permisosRol,
      });
      toast.success('Permisos del rol actualizados correctamente');
      await recargarRoles();
    } catch (error) {
      console.error('Error al guardar permisos del rol:', error);
      const msg = error.response?.data?.error || 'Error al guardar permisos del rol';
      toast.error(msg);
    } finally {
      setGuardando(false);
    }
  };

  // Seleccionar usuario para editar permisos
  const handleSeleccionarUsuario = useCallback(async (usuario) => {
    setUsuarioSeleccionado(usuario);
    try {
      const res = await instanciaAxios.get(`/permiso/usuario/${usuario.id_entidad}`);
      setPermisosUsuarioRol(res.data.permisos_rol.map((p) => p.id_permiso));
      setPermisosUsuarioIndividuales(res.data.permisos_individuales.map((p) => p.id_permiso));
      setNombreRolUsuario(res.data.nombre_rol || '');
      setIdTipoUsuarioActual(res.data.id_tipo_usuario || null);
    } catch (error) {
      toast.error('Error al cargar permisos del usuario');
    }
  }, []);

  // Toggle permiso individual del usuario
  const handleTogglePermisoUsuario = useCallback((idPermiso, checked) => {
    setPermisosUsuarioIndividuales((prev) =>
      checked ? [...prev, idPermiso] : prev.filter((id) => id !== idPermiso)
    );
  }, []);

  // Guardar permisos del usuario
  const handleGuardarUsuario = async () => {
    if (guardando || !usuarioSeleccionado) return;
    setGuardando(true);
    try {
      await instanciaAxios.put(`/permiso/usuario/${usuarioSeleccionado.id_entidad}`, {
        permisos: permisosUsuarioIndividuales,
      });
      toast.success('Permisos del usuario actualizados correctamente');
    } catch (error) {
      console.error('Error al guardar permisos del usuario:', error);
      toast.error('Error al guardar permisos del usuario');
    } finally {
      setGuardando(false);
    }
  };

  // Cambiar rol del usuario
  const handleCambiarRolUsuario = async (nuevoIdTipoUsuario) => {
    if (!usuarioSeleccionado || !nuevoIdTipoUsuario) return;
    const nuevoRol = roles.find(r => r.id_tipo_usuario === parseInt(nuevoIdTipoUsuario));
    if (!nuevoRol) return;

    const confirmar = window.confirm(
      `¿Cambiar el rol de "${usuarioSeleccionado.nombre} ${usuarioSeleccionado.apellido}" a "${nuevoRol.descripcion}"?`
    );
    if (!confirmar) return;

    try {
      await instanciaAxios.put(`/permiso/usuario/${usuarioSeleccionado.id_entidad}/rol`, {
        id_tipo_usuario: parseInt(nuevoIdTipoUsuario)
      });
      toast.success(`Rol cambiado a "${nuevoRol.descripcion}" correctamente`);
      // Recargar permisos del usuario (el rol heredado cambia)
      await handleSeleccionarUsuario({ ...usuarioSeleccionado, id_tipo_usuario: parseInt(nuevoIdTipoUsuario), nombre_rol: nuevoRol.descripcion });
      await recargarRoles();
      // Refrescar la lista de usuarios para que refleje el cambio de rol
      await cargarUsuarios();
    } catch (error) {
      const msg = error.response?.data?.error || 'Error al cambiar rol del usuario';
      toast.error(msg);
    }
  };

  // ---- Crear Usuario ----

  const resetNuevoUsuario = () => {
    setNuevoUsuario({ nombre: '', apellido: '', correo: '', username: '', clave: '', confirmarClave: '', id_tipo_usuario: '' });
  };

  const handleAbrirModalCrearUsuario = () => {
    resetNuevoUsuario();
    // Default to first non-admin role
    const primerRolNoAdmin = roles.find(r => !ES_ADMIN(r));
    if (primerRolNoAdmin) {
      setNuevoUsuario(prev => ({ ...prev, id_tipo_usuario: primerRolNoAdmin.id_tipo_usuario }));
    }
    setModalCrearUsuario(true);
  };

  const handleCampoNuevoUsuario = (campo, valor) => {
    setNuevoUsuario(prev => ({ ...prev, [campo]: valor }));
  };

  const handleCrearUsuario = async () => {
    const { nombre, apellido, correo, username, clave, confirmarClave, id_tipo_usuario } = nuevoUsuario;

    if (!nombre.trim()) return toast.error('El nombre es obligatorio');
    if (!apellido.trim()) return toast.error('El apellido es obligatorio');
    if (!correo.trim()) return toast.error('El correo es obligatorio');
    if (!username.trim()) return toast.error('El nombre de usuario es obligatorio');
    if (!/^[a-z0-9._-]+$/.test(username.trim().toLowerCase())) {
      return toast.error('El nombre de usuario solo puede contener letras minúsculas, números, puntos, guiones y guiones bajos');
    }
    if (!clave) return toast.error('La clave es obligatoria');
    if (clave.length < 4) return toast.error('La clave debe tener al menos 4 caracteres');
    if (clave !== confirmarClave) return toast.error('Las claves no coinciden');
    if (!id_tipo_usuario) return toast.error('Debe seleccionar un rol');

    setCreandoUsuario(true);
    try {
      const res = await instanciaAxios.post('/permiso/usuario', {
        nombre: nombre.trim(),
        apellido: apellido.trim(),
        correo: correo.trim(),
        username: username.trim().toLowerCase(),
        clave,
        id_tipo_usuario: parseInt(id_tipo_usuario)
      });

      toast.success(`Usuario "${res.data.usuario}" creado correctamente`);
      setModalCrearUsuario(false);
      resetNuevoUsuario();
      await Promise.all([cargarUsuarios(), cargarTenantInfo(), recargarRoles()]);
    } catch (error) {
      console.error('Error al crear usuario:', error.response?.data);
      const data = error.response?.data || {};
      const msg = data.error || 'Error al crear usuario';
      const faltantes = data.permisos_faltantes;
      if (faltantes?.length > 0) {
        toast.error(`${msg}. Permisos faltantes: ${faltantes.join(', ')}`, { autoClose: 10000 });
      } else {
        toast.error(msg, { autoClose: 8000 });
      }
    } finally {
      setCreandoUsuario(false);
    }
  };

  const limiteAlcanzado = tenantInfo && tenantInfo.usuarios_actuales >= tenantInfo.limite_usuarios;

  // ---- Gestión de Roles ----

  const handleCrearRol = async () => {
    if (!nuevoRolNombre.trim()) return;
    setCreandoRol(true);
    try {
      await instanciaAxios.post('/permiso/tipo-usuario', { descripcion: nuevoRolNombre.trim() });
      toast.success(`Rol "${nuevoRolNombre.trim()}" creado correctamente`);
      setNuevoRolNombre('');
      await recargarRoles();
    } catch (error) {
      console.error('Error al crear rol:', error.response?.data);
      const data = error.response?.data || {};
      const msg = data.error || 'Error al crear rol';
      const faltantes = data.permisos_faltantes;
      if (faltantes?.length > 0) {
        toast.error(`${msg}. Permisos faltantes: ${faltantes.join(', ')}. Cierra sesión y vuelve a iniciar, o ejecuta la migración de permisos.`, { autoClose: 15000 });
      } else {
        toast.error(msg);
      }
    } finally {
      setCreandoRol(false);
    }
  };

  const handleRenombrarRol = async (idTipoUsuario) => {
    if (!editandoRolNombre.trim()) return;
    try {
      await instanciaAxios.put(`/permiso/tipo-usuario/${idTipoUsuario}/nombre`, {
        descripcion: editandoRolNombre.trim()
      });
      toast.success('Rol renombrado correctamente');
      setEditandoRolId(null);
      setEditandoRolNombre('');
      await recargarRoles();
    } catch (error) {
      const msg = error.response?.data?.error || 'Error al renombrar rol';
      toast.error(msg);
    }
  };

  const handleEliminarRol = async (rol) => {
    const confirmar = window.confirm(
      `¿Eliminar el rol "${rol.descripcion}"? Esta acción desactivará el rol.`
    );
    if (!confirmar) return;
    try {
      await instanciaAxios.delete(`/permiso/tipo-usuario/${rol.id_tipo_usuario}`);
      toast.success(`Rol "${rol.descripcion}" eliminado correctamente`);
      await recargarRoles();
    } catch (error) {
      const msg = error.response?.data?.error || 'Error al eliminar rol';
      toast.error(msg);
    }
  };

  if (cargando) return <SpinnerCargando />;

  // Roles no-admin para dropdown de cambio de rol
  const rolesNoAdmin = roles.filter(r => !ES_ADMIN(r));

  // Dock handler: contextual save based on active tab
  const handleDockGuardar = () => {
    if (tabActivo === 'roles' && rolSeleccionado) {
      handleGuardarRol();
    } else if (tabActivo === 'usuarios' && usuarioSeleccionado) {
      handleGuardarUsuario();
    }
  };

  // Show dock save only when there's something to save
  const mostrarDockGuardar =
    (tabActivo === 'roles' && rolSeleccionado) ||
    (tabActivo === 'usuarios' && usuarioSeleccionado);

  return (
    <div className="max-w-5xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="bg-color-primario bg-opacity-10 rounded-full p-3">
          <LuKeyRound className="text-color-primario text-2xl" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Administración de Permisos</h1>
          <p className="text-sm text-gray-500">Gestiona roles, permisos por rol o por usuario individual</p>
        </div>
      </div>

      {/* Tabs — system pattern */}
      <div className="grid grid-cols-3 sm:grid-cols-3 gap-3 mb-6 max-w-lg">
        {[
          { id: 'roles', label: 'Por Rol' },
          { id: 'usuarios', label: 'Por Usuario' },
          { id: 'gestionRoles', label: 'Roles' },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setTabActivo(tab.id)}
            className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors duration-200 ${
              tabActivo === tab.id
                ? 'bg-color-secundario text-white border-color-secundario'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ============================================ */}
      {/* Tab: Por Rol (permisos)                      */}
      {/* ============================================ */}
      {tabActivo === 'roles' && (
        <div className="space-y-4">
          {/* Select de rol */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tipo de usuario (rol)
            </label>
            <select
              value={rolSeleccionado || ''}
              onChange={(e) => handleCambiarRol(parseInt(e.target.value))}
              className="w-full max-w-sm border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-color-secundario/30 focus:border-color-secundario outline-none"
            >
              {roles.map((rol) => (
                <option key={rol.id_tipo_usuario} value={rol.id_tipo_usuario}>
                  {rol.descripcion} {ES_ADMIN(rol) ? '(protegido)' : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Admin guard banner */}
          {esAdminSeleccionado && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
              <LuShieldAlert className="text-amber-500 text-xl mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-amber-700">Rol protegido</p>
                <p className="text-xs text-amber-600 mt-1">
                  Al rol administrador no se le pueden quitar permisos que ya tiene, pero sí se le pueden agregar nuevos.
                </p>
              </div>
            </div>
          )}

          {/* Grid de permisos */}
          <GridPermisos
            agrupados={permisosAgrupados}
            seleccionados={permisosRol}
            heredados={esAdminSeleccionado ? permisosRolOriginal : []}
            disabled={false}
            onChange={handleTogglePermisoRol}
          />
        </div>
      )}

      {/* ============================================ */}
      {/* Tab: Por Usuario                              */}
      {/* ============================================ */}
      {tabActivo === 'usuarios' && (
        <div className="space-y-4">
          {/* Tenant info bar */}
          {tenantInfo && (
            <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-4">
                {tenantInfo.dominio && (
                  <div className="flex items-center gap-2 text-sm">
                    <FaAt className="text-color-primario opacity-60" />
                    <span className="text-gray-500">Dominio:</span>
                    <span className="font-semibold text-color-primario">{tenantInfo.dominio}</span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-sm">
                  <FaUsers className="text-color-primario opacity-60" />
                  <span className="text-gray-500">Usuarios:</span>
                  <span className={`font-semibold ${limiteAlcanzado ? 'text-red-600' : 'text-color-primario'}`}>
                    {tenantInfo.usuarios_actuales}/{tenantInfo.limite_usuarios}
                  </span>
                </div>
              </div>
              <button
                onClick={handleAbrirModalCrearUsuario}
                disabled={limiteAlcanzado || !tenantInfo.dominio}
                title={
                  limiteAlcanzado
                    ? `Límite de ${tenantInfo.limite_usuarios} usuarios alcanzado`
                    : !tenantInfo.dominio
                    ? 'Se requiere un dominio configurado para crear usuarios'
                    : 'Crear nuevo usuario'
                }
                className="flex items-center gap-2 px-4 py-2 bg-color-secundario text-white rounded-lg hover:bg-opacity-90 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FaUserPlus />
                Crear usuario
              </button>
            </div>
          )}

          {/* Lista de usuarios con filtro */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-medium text-gray-700">
                Seleccionar usuario
              </label>
              <span className="text-xs text-gray-400">
                {listaUsuarios.length} usuario{listaUsuarios.length !== 1 ? 's' : ''} activo{listaUsuarios.length !== 1 ? 's' : ''}
              </span>
            </div>

            {/* Filtro */}
            <div className="relative max-w-sm mb-3">
              <LuSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
              <input
                type="text"
                value={filtroUsuario}
                onChange={(e) => setFiltroUsuario(e.target.value)}
                placeholder="Filtrar por nombre, usuario o rol..."
                className="w-full border border-gray-300 rounded-lg pl-9 pr-3 py-2 text-sm focus:ring-2 focus:ring-color-secundario/30 focus:border-color-secundario outline-none"
              />
            </div>

            {cargandoUsuarios ? (
              <div className="text-center py-8 text-gray-400">
                <SpinnerCargando />
              </div>
            ) : (
              <div className="border border-gray-200 rounded-lg overflow-hidden max-h-64 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-color-primario text-white sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium">Nombre</th>
                      <th className="text-left px-3 py-2 font-medium">Usuario</th>
                      <th className="text-left px-3 py-2 font-medium">Rol</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usuariosFiltrados.map((u) => {
                      const seleccionado = usuarioSeleccionado?.id_entidad === u.id_entidad;
                      const esAdmin = u.nombre_rol === 'administrador';
                      return (
                        <tr
                          key={u.id_entidad}
                          onClick={() => handleSeleccionarUsuario(u)}
                          className={`cursor-pointer border-t border-gray-100 transition-colors ${
                            seleccionado
                              ? 'bg-emerald-50 border-l-2 border-l-color-secundario'
                              : 'hover:bg-gray-50'
                          }`}
                        >
                          <td className="px-3 py-2.5">
                            <div className="flex items-center gap-2">
                              {esAdmin && <FaUserShield className="text-amber-500 text-xs flex-shrink-0" title="Administrador" />}
                              <span className="font-medium text-gray-700">{u.nombre} {u.apellido}</span>
                            </div>
                          </td>
                          <td className="px-3 py-2.5 text-gray-500">{u.usuario}</td>
                          <td className="px-3 py-2.5">
                            <span className={`inline-block text-xs px-2 py-0.5 rounded-full ${
                              esAdmin
                                ? 'bg-amber-100 text-amber-700'
                                : 'bg-color-primario bg-opacity-10 text-color-primario'
                            }`}>
                              {u.nombre_rol}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                    {usuariosFiltrados.length === 0 && (
                      <tr>
                        <td colSpan={3} className="text-center py-6 text-gray-400">
                          {filtroUsuario ? 'Sin resultados para el filtro' : 'No hay usuarios activos'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Permisos del usuario seleccionado */}
          {usuarioSeleccionado && (
            <>
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 space-y-3">
                <p className="text-sm text-color-primario">
                  <span className="font-semibold">Usuario:</span> {usuarioSeleccionado.nombre} {usuarioSeleccionado.apellido}
                  {nombreRolUsuario && (
                    <span className="ml-3 bg-color-primario bg-opacity-10 text-color-primario px-2 py-0.5 rounded-full text-xs">
                      Rol: {nombreRolUsuario}
                    </span>
                  )}
                </p>

                {/* Cambiar rol */}
                {nombreRolUsuario === 'administrador' ? (
                  <div className="flex items-center gap-2 text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg p-2">
                    <FaLock className="flex-shrink-0" />
                    <span>A un usuario administrador no se le puede quitar ese rol desde la interfaz.</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <label className="text-sm text-color-primario font-medium whitespace-nowrap">Cambiar rol:</label>
                    <select
                      value={idTipoUsuarioActual || ''}
                      onChange={(e) => handleCambiarRolUsuario(e.target.value)}
                      className="border border-emerald-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-color-secundario/30 focus:border-color-secundario outline-none bg-white"
                    >
                      {roles.map((rol) => (
                        <option key={rol.id_tipo_usuario} value={rol.id_tipo_usuario}>
                          {rol.descripcion}{ES_ADMIN(rol) ? ' (administrador)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <p className="text-xs text-color-secundario">
                  Los permisos marcados como "heredado" vienen del rol y no pueden modificarse aquí.
                  Solo se pueden agregar permisos adicionales individuales.
                </p>
              </div>

              <GridPermisos
                agrupados={permisosAgrupados}
                seleccionados={permisosUsuarioIndividuales}
                heredados={permisosUsuarioRol}
                disabled={false}
                onChange={handleTogglePermisoUsuario}
              />
            </>
          )}

          {!usuarioSeleccionado && !cargandoUsuarios && (
            <div className="text-center py-12 text-gray-400">
              <FaUsers className="text-5xl mx-auto mb-3 opacity-40" />
              <p>Selecciona un usuario de la lista para administrar sus permisos</p>
            </div>
          )}
        </div>
      )}

      {/* ============================================ */}
      {/* Tab: Gestión de Roles                         */}
      {/* ============================================ */}
      {tabActivo === 'gestionRoles' && (
        <div className="space-y-4">
          {/* Crear nuevo rol */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Crear nuevo rol
            </label>
            <div className="flex gap-2 max-w-md">
              <input
                type="text"
                value={nuevoRolNombre}
                onChange={(e) => setNuevoRolNombre(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCrearRol()}
                placeholder="Nombre del nuevo rol..."
                className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-color-secundario/30 focus:border-color-secundario outline-none"
              />
              <button
                onClick={handleCrearRol}
                disabled={creandoRol || !nuevoRolNombre.trim()}
                className="flex items-center gap-2 px-4 py-2 bg-color-secundario text-white rounded-lg hover:bg-opacity-90 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FaPlus />
                {creandoRol ? 'Creando...' : 'Crear'}
              </button>
            </div>
          </div>

          {/* Lista de roles */}
          <div className="space-y-2">
            {roles.map((rol) => {
              const admin = ES_ADMIN(rol);
              const editando = editandoRolId === rol.id_tipo_usuario;

              return (
                <div
                  key={rol.id_tipo_usuario}
                  className={`bg-white border rounded-xl p-4 flex items-center justify-between transition-colors ${
                    admin ? 'border-amber-200 bg-amber-50/30' : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    {admin ? (
                      <div className="bg-amber-100 rounded-full p-2">
                        <FaLock className="text-amber-500 text-sm" />
                      </div>
                    ) : (
                      <div className="bg-color-primario bg-opacity-10 rounded-full p-2">
                        <FaUsers className="text-color-secundario text-sm" />
                      </div>
                    )}

                    <div className="flex-1 min-w-0">
                      {editando ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={editandoRolNombre}
                            onChange={(e) => setEditandoRolNombre(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleRenombrarRol(rol.id_tipo_usuario);
                              if (e.key === 'Escape') { setEditandoRolId(null); setEditandoRolNombre(''); }
                            }}
                            autoFocus
                            className="border border-color-secundario rounded-lg px-2 py-1 text-sm focus:ring-2 focus:ring-color-secundario/30 focus:border-color-secundario outline-none"
                          />
                          <button
                            onClick={() => handleRenombrarRol(rol.id_tipo_usuario)}
                            className="text-color-secundario hover:text-color-primario text-sm font-medium"
                          >
                            Guardar
                          </button>
                          <button
                            onClick={() => { setEditandoRolId(null); setEditandoRolNombre(''); }}
                            className="text-gray-400 hover:text-gray-600 text-sm"
                          >
                            Cancelar
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-gray-700 capitalize">{rol.descripcion}</span>
                          {admin && (
                            <span className="text-xs bg-amber-100 text-amber-600 px-2 py-0.5 rounded-full">
                              protegido
                            </span>
                          )}
                        </div>
                      )}
                      <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                        <span>{rol.cantidad_usuarios || 0} usuario{(rol.cantidad_usuarios || 0) !== 1 ? 's' : ''}</span>
                        <span className="text-gray-300">|</span>
                        <span>{rol.permisos?.length || 0} permisos</span>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  {!admin && !editando && (
                    <div className="flex items-center gap-2 ml-3">
                      <button
                        onClick={() => {
                          setEditandoRolId(rol.id_tipo_usuario);
                          setEditandoRolNombre(rol.descripcion);
                        }}
                        className="p-2 text-gray-400 hover:text-color-secundario hover:bg-emerald-50 rounded-lg transition-colors"
                        title="Renombrar rol"
                      >
                        <FaPen className="text-sm" />
                      </button>
                      <button
                        onClick={() => handleEliminarRol(rol)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Eliminar rol"
                      >
                        <FaTrash className="text-sm" />
                      </button>
                    </div>
                  )}
                </div>
              );
            })}

            {roles.length === 0 && (
              <div className="text-center py-12 text-gray-400">
                <FaUsers className="text-5xl mx-auto mb-3 opacity-40" />
                <p>No hay roles configurados</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Dock de acciones */}
      <DockVerticalDerecha
        handleBotonGuardar={mostrarDockGuardar ? handleDockGuardar : undefined}
        guardando={guardando}
      />

      {/* ============================================ */}
      {/* Modal: Crear Usuario                          */}
      {/* ============================================ */}
      {modalCrearUsuario && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
            {/* Header */}
            <div className="bg-color-primario px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FaUserPlus className="text-white text-lg" />
                <h2 className="text-lg font-bold text-white">Crear nuevo usuario</h2>
              </div>
              <button
                onClick={() => setModalCrearUsuario(false)}
                className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-1.5 transition-colors"
              >
                <FaTimes />
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-5 space-y-4 max-h-[70vh] overflow-y-auto">
              {/* Nombre + Apellido */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
                  <input
                    type="text"
                    value={nuevoUsuario.nombre}
                    onChange={(e) => handleCampoNuevoUsuario('nombre', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-color-secundario/30 focus:border-color-secundario outline-none"
                    placeholder="Nombre"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Apellido *</label>
                  <input
                    type="text"
                    value={nuevoUsuario.apellido}
                    onChange={(e) => handleCampoNuevoUsuario('apellido', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-color-secundario/30 focus:border-color-secundario outline-none"
                    placeholder="Apellido"
                  />
                </div>
              </div>

              {/* Correo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Correo electrónico *</label>
                <input
                  type="email"
                  value={nuevoUsuario.correo}
                  onChange={(e) => handleCampoNuevoUsuario('correo', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-color-secundario/30 focus:border-color-secundario outline-none"
                  placeholder="correo@ejemplo.com"
                />
              </div>

              {/* Username con dominio */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre de usuario *</label>
                <div className="flex items-stretch">
                  <input
                    type="text"
                    value={nuevoUsuario.username}
                    onChange={(e) => handleCampoNuevoUsuario('username', e.target.value.toLowerCase().replace(/[^a-z0-9._-]/g, ''))}
                    className="flex-1 border border-gray-300 rounded-l-lg px-3 py-2 text-sm focus:ring-2 focus:ring-color-secundario/30 focus:border-color-secundario outline-none"
                    placeholder="nombre.usuario"
                  />
                  <span className="bg-gray-100 border border-l-0 border-gray-300 rounded-r-lg px-3 py-2 text-sm text-gray-500 font-medium flex items-center whitespace-nowrap">
                    @{tenantInfo?.dominio || '...'}
                  </span>
                </div>
                {nuevoUsuario.username && tenantInfo?.dominio && (
                  <p className="text-xs text-gray-400 mt-1">
                    El usuario iniciará sesión como: <span className="font-medium text-color-primario">{nuevoUsuario.username.toLowerCase()}@{tenantInfo.dominio}</span>
                  </p>
                )}
              </div>

              {/* Clave + Confirmar */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Clave *</label>
                  <input
                    type="password"
                    value={nuevoUsuario.clave}
                    onChange={(e) => handleCampoNuevoUsuario('clave', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-color-secundario/30 focus:border-color-secundario outline-none"
                    placeholder="Clave"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Confirmar clave *</label>
                  <input
                    type="password"
                    value={nuevoUsuario.confirmarClave}
                    onChange={(e) => handleCampoNuevoUsuario('confirmarClave', e.target.value)}
                    className={`w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-color-secundario/30 outline-none ${
                      nuevoUsuario.confirmarClave && nuevoUsuario.clave !== nuevoUsuario.confirmarClave
                        ? 'border-red-300 focus:border-red-400'
                        : 'border-gray-300 focus:border-color-secundario'
                    }`}
                    placeholder="Confirmar"
                  />
                  {nuevoUsuario.confirmarClave && nuevoUsuario.clave !== nuevoUsuario.confirmarClave && (
                    <p className="text-xs text-red-500 mt-1">Las claves no coinciden</p>
                  )}
                </div>
              </div>

              {/* Rol */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rol *</label>
                <select
                  value={nuevoUsuario.id_tipo_usuario}
                  onChange={(e) => handleCampoNuevoUsuario('id_tipo_usuario', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-color-secundario/30 focus:border-color-secundario outline-none"
                >
                  <option value="">Seleccionar rol...</option>
                  {rolesNoAdmin.map((rol) => (
                    <option key={rol.id_tipo_usuario} value={rol.id_tipo_usuario}>
                      {rol.descripcion}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setModalCrearUsuario(false)}
                className="px-4 py-2 text-sm text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleCrearUsuario}
                disabled={creandoUsuario}
                className="flex items-center gap-2 px-5 py-2 bg-color-secundario text-white rounded-lg hover:bg-opacity-90 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FaUserPlus />
                {creandoUsuario ? 'Creando...' : 'Crear usuario'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
